import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import {
  MatLegacySelectModule as MatSelectModule,
  MatLegacySelectChange as MatSelectChange,
} from '@angular/material/legacy-select';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

// MODULES
import { SalesOrderSourcePlatform } from '@app/core/sales/store/sales-order.model';
import { PartialsModule } from '@app/layout-sidebar/partials/partials.module';
import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';
import { IGetApiResponse } from 'v2/modules/shared/interfaces/api-response.interface';
import { IAppIntegrationAccount } from 'v2/modules/shared/interfaces/app-integration-account.interface';
import { PipeModule } from 'v2/modules/shared/pipes/pipe.module';

// SERVICES
import { AppIntegrationAccountApiService } from 'v2/modules/shared/services/api/app-integration/app-integration-account-api.service';
import { SalesOrderApiService } from 'v2/modules/shared/services/api/sales-order-api.service';
import { LoadingDialogUtilService } from 'v2/modules/shared/services/util/loading-dialog-util.service';

import { IOmsStore, OmsStoreService } from '../../../shared';

// INTERFACES

import { trackById } from '../../../shared/util/track-by.util';
import { EAppAccountType } from '../../../system-setting/constants';

// CONFIGS

// UTILS

interface JsonNode {
  key :string;
  value :any;
  level :number;
  expanded :boolean;
  hasChildren :boolean;
  isArray :boolean;
  isLastChild :boolean;
  childCount :number;
}

interface AnalysisResult {
  field :string;
  issue :string;
  suggestion :string;
}

interface TestMappingResult {
  sourceField :string;
  sourceExists :boolean;
  extractedValue :any;
  targetField :string;
  mappedValue :any;
  success :boolean;
  suggestion ?:string;
}

@Component({
  selector: 'lum-sales-order-pull-order-form',
  templateUrl: './sales-order-pull-order-form.component.html',
  styleUrls: ['./sales-order-pull-order-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatSelectModule,
    ReactiveFormsModule,
    PipeModule,
    PartialsModule,
    TranslateModule,
    MatDividerModule,
    MatCheckboxModule,
    MatIconModule,
    MatTabsModule,
  ],
})
export class SalesOrderPullOrderFormComponent implements OnInit {
  form :UntypedFormGroup;
  private onDestroy$ = new Subject();
  appIntegrationAccounts :IAppIntegrationAccount[] = [];
  trackById = trackById;
  loading :boolean;
  rawOrderObject :object = {};
  omsStores :object = {};

  // JSON viewer properties
  jsonNodes :JsonNode[] = [];
  expandAll :boolean = true;

  // AI debugging results
  markdownAnalysis :SafeHtml = null;
  testMappingResult :TestMappingResult = null;

  // Tab control
  activeTabIndex = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data :any,
    private dialogRef :MatDialogRef<SalesOrderPullOrderFormComponent>,
    private fb :UntypedFormBuilder,
    private snackBar :MatSnackBar,
    private appIntegrationAccountService :AppIntegrationAccountApiService,
    private salesOrderApiService :SalesOrderApiService,
    private loadingDialogService :LoadingDialogUtilService,
    private snackbar :MatSnackBar,
    private omsStoreService :OmsStoreService,
    private sanitizer :DomSanitizer,
  ) {}

  ngOnInit() :void {
    this.getAppIntegrationAccounts();
    this.initForm();
  }

  onSubmit() :void {
    if (this.form.invalid) {
      this.dialogRef.close();
    }

    const data = this.form.value;
    if (!this.isOmsStoreRequired) {
      delete data.omsStoreId;
    }

    this.loading = true;
    this.loadingDialogService.show('Pulling order...');

    // Reset AI debugging results
    this.markdownAnalysis = null;
    this.testMappingResult = null;

    // Build payload with appropriate options
    const payload = { ...data };

    // Handle apply mapping
    if (payload.applyMapping) {
      payload.apply_mapping = true;
    }
    delete payload.applyMapping;

    // Handle analyze mapping
    if (payload.analyzeMapping) {
      payload.analyze_mapping = true;
    }
    delete payload.analyzeMapping;

    // Handle test mapping
    if (payload.testMapping && payload.sourceField && payload.targetField) {
      payload.test_mapping = true;
      payload.source_field = payload.sourceField;
      payload.target_field = payload.targetField;
    }
    delete payload.testMapping;
    delete payload.sourceField;
    delete payload.targetField;

    this.salesOrderApiService
      .pullOrder(payload)
      .pipe(take(1))
      .subscribe({
        next: (response :any) => {
          this.loadingDialogService.close();
          this.snackbar.open(
            response.success ?? 'Successfully pulled order from OMS.',
            'Success',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );

          // Store the response data
          this.rawOrderObject = response ?? {};

          // Process analysis results if present
          if (response.analysis) {
            this.processMarkdownAnalysis(response.analysis);
            // If analysis exists, switch to the analysis tab
            this.setActiveTab(1); // 1 is the index for the analysis tab
          }

          // Process test mapping results if present
          if (response.test_mapping_result) {
            this.testMappingResult = response.test_mapping_result;
            // If test mapping results exist, switch to the test results tab
            this.setActiveTab(2); // 2 is the index for the test results tab
          }

          // Process the JSON data for the viewer
          this.processJsonData();
          this.loading = false;
        },
        error: (response :any) => {
          this.loadingDialogService.close();
          this.snackbar.open(response.error ?? 'Failed to pull order.', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });

          this.rawOrderObject = {};
          this.jsonNodes = [];
          this.markdownAnalysis = null;
          this.testMappingResult = null;
          this.loading = false;
        },
      });
  }

  onClose() :void {
    this.dialogRef.close();
  }

  onAppIntegrationAccountChange(event :MatSelectChange) :void {
    this.getOmsStores();
    this.resetOmsStoreIdField();
  }

  onTestMappingChange(event :MatCheckboxChange) :void {
    if (event.checked) {
      // Enable source and target field controls
      this.form.get('sourceField').enable();
      this.form.get('targetField').enable();
    } else {
      // Disable source and target field controls
      this.form.get('sourceField').disable();
      this.form.get('targetField').disable();
    }
  }

  /**
   * Set the active tab and handle tab changes
   */
  setActiveTab(index :number) :void {
    this.activeTabIndex = index;
  }

  /**
   * Process markdown analysis text and convert it to HTML
   */
  private processMarkdownAnalysis(markdownText :string) :void {
    if (!markdownText) {
      this.markdownAnalysis = null;

      return;
    }

    // Convert markdown to HTML
    const html = this.markdownToHtml(markdownText);

    // Sanitize the HTML to prevent XSS
    this.markdownAnalysis = this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /**
   * Convert markdown text to HTML
   */
  private markdownToHtml(markdown :string) :string {
    if (!markdown) return '';

    // Process headers (### Heading -> <h3>Heading</h3>)
    let html = markdown.replace(/### (.*)/g, '<h3>$1</h3>');
    html = html.replace(/## (.*)/g, '<h2>$1</h2>');
    html = html.replace(/# (.*)/g, '<h1>$1</h1>');

    // Process bold (**text** -> <strong>text</strong>)
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Process italic (*text* -> <em>text</em>)
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');

    // Process code blocks (```code``` -> <pre><code>code</code></pre>)
    html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');

    // Process inline code (`code` -> <code>code</code>)
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

    // Process unordered lists
    // First, find list items
    html = html.replace(/- (.*)/g, '<li>$1</li>');

    // Then wrap consecutive list items in <ul> tags
    html = html.replace(/(<li>.*<\/li>)(\s*)(<li>)/g, '$1\n<ul>$3');
    html = html.replace(/(<\/li>)(\s*)(?!<li>|<ul>)/g, '$1\n</ul>');

    // Process ordered lists
    // First, find list items
    html = html.replace(/\d+\. (.*)/g, '<li>$1</li>');

    // Then wrap consecutive list items in <ol> tags
    html = html.replace(/(<li>.*<\/li>)(\s*)(<li>)/g, '$1\n<ol>$3');
    html = html.replace(/(<\/li>)(\s*)(?!<li>|<ol>)/g, '$1\n</ol>');

    // Process paragraphs (double newlines -> <p> tags)
    html = html.replace(/\n\s*\n/g, '\n<p>\n');

    // Process links
    html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2">$1</a>');

    return html;
  }

  private getAppIntegrationAccounts() :void {
    const params :{ [key :string] :string } = {
      withPullOrdersChannelIntegrationMapping: 'true',
      limit: '100',
    };

    this.appIntegrationAccountService
      .get(params)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :IGetApiResponse) => {
          this.appIntegrationAccounts = response.data;
        },
        error: (response :any) => {
          const message = response.error ?? 'Failed to pull oms accounts.';
          this.snackBar.open(message, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private initForm() :void {
    this.form = this.fb.group({
      appIntegrationAccountId: [null, [Validators.required]],
      orderId: [null, [Validators.required]],
      omsStoreId: [null, [Validators.required, this.omsStoreIdValidator()]],
      applyMapping: [false],

      // AI debugging options
      analyzeMapping: [false],
      testMapping: [false],
      sourceField: [{ value: '', disabled: true }],
      targetField: [{ value: '', disabled: true }],
    });
  }

  private omsStoreIdValidator() :ValidatorFn {
    return (control :AbstractControl) :ValidationErrors | null => {
      if (!this.hasAppIntegrationAccountSelected) {
        return null;
      }

      return { omsStoreId: true };
    };
  }

  private getOmsStores() :void {
    if (
      !this.hasAppIntegrationAccountSelected ||
      this.omsStores[this.selectedAppIntegrationAccount.id]
    ) {
      return;
    }

    this.loading = true;
    this.omsStoreService
      .get(this.selectedAppIntegrationAccount.id, { limit: '100' })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :IGetApiResponse) => {
          this.omsStores[this.selectedAppIntegrationAccount.id] = response.data;
          this.loading = false;
        },
        error: (e) => {
          this.loading = false;
        },
      });
  }

  private resetOmsStoreIdField() :void {
    if (this.isOmsStoreRequired) {
      this.omsStoreIdControl?.setValidators(Validators.required);
    } else {
      this.omsStoreIdControl?.clearValidators();
      this.omsStoreIdControl?.setValue(null);
    }
    this.omsStoreIdControl?.updateValueAndValidity();
  }

  get hasAppIntegrationAccountSelected() :boolean {
    if (!this.form?.value) {
      return false;
    }

    return (
      this.form.value?.appIntegrationAccountId !== null &&
      this.form.value?.appIntegrationAccountId > 0
    );
  }

  get selectedAppIntegrationAccountIdFieldLabel() :string {
    const appIntegrationAccountId = this.form.value.appIntegrationAccountId;
    if (appIntegrationAccountId === null || appIntegrationAccountId <= 0) {
      return 'Order ID';
    }

    const appIntegrationAccount = this.appIntegrationAccounts.find(
      (account) => account.id === appIntegrationAccountId,
    );

    const omsThatRequireOrderNumber :string[] = [EAppAccountType.SHIPKEY];
    if (omsThatRequireOrderNumber.includes(appIntegrationAccount.app)) {
      return 'Order Number';
    }

    const omsThatRequireSourceReferenceId :string[] = [
      EAppAccountType.NUORDER,
      EAppAccountType.TRACKSTAR,
    ];
    if (omsThatRequireSourceReferenceId.includes(appIntegrationAccount.app)) {
      return 'Source Reference ID';
    }

    return 'Order ID';
  }

  get selectedAppIntegrationAccount() :IAppIntegrationAccount | null {
    const appIntegrationAccountId = this.form.value.appIntegrationAccountId;
    if (!appIntegrationAccountId) {
      return null;
    }

    return this.appIntegrationAccounts.find(
      (account) => account.id === appIntegrationAccountId,
    );
  }

  get isOmsStoreRequired() :boolean {
    if (!this.hasAppIntegrationAccountSelected) {
      return false;
    }

    return (
      this.selectedAppIntegrationAccount?.app === SalesOrderSourcePlatform.SHIPKEY
    );
  }

  get omsStoreIdControl() :UntypedFormControl {
    return this.form.controls.omsStoreId as UntypedFormControl;
  }

  get selectedAccountOmsStores() :IOmsStore[] {
    return this.omsStores[this.selectedAppIntegrationAccount.id] ?? [];
  }

  // JSON viewer methods
  get hasOrderData() :boolean {
    return this.jsonNodes.length > 0;
  }

  processJsonData() :void {
    this.jsonNodes = [];
    if (!this.rawOrderObject || Object.keys(this.rawOrderObject).length === 0) {
      return;
    }

    this.parseObject(this.rawOrderObject, '', 0, true);
  }

  private parseObject(
    obj :any,
    key :string,
    level :number,
    isExpanded :boolean,
  ) :void {
    if (typeof obj !== 'object' || obj === null) {
      return;
    }

    const isArray = Array.isArray(obj);
    const keys = Object.keys(obj);
    const childCount = keys.length;

    // Add the parent node
    this.jsonNodes.push({
      key: key,
      value: obj,
      level: level,
      expanded: isExpanded,
      hasChildren: childCount > 0,
      isArray,
      isLastChild: false,
      childCount,
    });

    if (isExpanded) {
      // Process children
      keys.forEach((childKey, index) => {
        const value = obj[childKey];
        const hasChildren =
          typeof value === 'object' &&
          value !== null &&
          Object.keys(value).length > 0;
        const isLastChild = index === keys.length - 1;

        if (hasChildren) {
          // If it's an object or array with children, recursively process it
          this.parseObject(value, childKey, level + 1, isExpanded);

          // Mark the last child
          if (isLastChild) {
            const lastNode = this.jsonNodes[this.jsonNodes.length - 1];
            this.jsonNodes[this.jsonNodes.length - 1] = {
              ...lastNode,
              isLastChild: true,
            };
          }
        } else {
          // It's a primitive value
          this.jsonNodes.push({
            key: childKey,
            value,
            level: level + 1,
            expanded: false,
            hasChildren: false,
            isArray: false,
            isLastChild,
            childCount: 0,
          });
        }
      });
    }
  }

  toggleNode(index :number) :void {
    const node = this.jsonNodes[index];
    if (!node.hasChildren) return;

    const expanded = !node.expanded;
    const currentLevel = node.level;

    // Update current node
    this.jsonNodes[index] = { ...node, expanded };

    if (expanded) {
      // Expanding - we need to insert child nodes
      const temp = [...this.jsonNodes];
      this.jsonNodes = temp.slice(0, index + 1);
      this.parseObject(node.value, node.key, node.level, true);
      // Keep the remaining nodes after the expanded section
      this.jsonNodes = [...this.jsonNodes, ...temp.slice(index + 1)];
    } else {
      // Collapsing - remove all child nodes
      let i = index + 1;
      while (i < this.jsonNodes.length && this.jsonNodes[i].level > currentLevel) {
        i++;
      }
      this.jsonNodes = [
        ...this.jsonNodes.slice(0, index + 1),
        ...this.jsonNodes.slice(i),
      ];
    }
  }

  toggleAllNodes() :void {
    this.expandAll = !this.expandAll;
    this.jsonNodes = [];
    this.processJsonData();
  }

  copyJsonToClipboard() :void {
    try {
      const jsonString = JSON.stringify(this.rawOrderObject, null, 2);
      navigator.clipboard.writeText(jsonString);
      this.snackBar.open('JSON copied to clipboard', 'Success', {
        duration: 2000,
      });
    } catch (error) {
      this.snackBar.open('Failed to copy JSON', 'Error', {
        duration: 2000,
      });
    }
  }

  getValueType(value :any) :string {
    if (value === null) return 'json-null';
    if (value === undefined) return 'json-undefined';
    if (typeof value === 'string') return 'json-string';
    if (typeof value === 'number') return 'json-number';
    if (typeof value === 'boolean') return 'json-boolean';

    return '';
  }

  getFormattedValue(value :any) :string {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') return `"${value}"`;

    return String(value);
  }
}
